import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row, FloatingLabel, Alert, Card, Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { logout, sendMultiFactorCode, verifyTwoFactor } from "../../services/httpClient";
import useApp from "../../utils/useApp";

const Authenticate = () => {
  const { auth, setAuth, setActivePage }: any = useApp();
  const [code, setCode] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [attempts, setAttempts] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasSentCode = useRef(false);

  useEffect(() => {
    inputRef?.current?.focus();

    if (!hasSentCode.current) {
      const method = auth?.profile?.default_2fa;
      if (method === "email" || method === "phone") {
        sendCode(method, false);
        hasSentCode.current = true;
      }
    }
  }, []);

  let navigate = useNavigate();

  const sendCode = (type: "email" | "phone", showMessage: boolean = true) => {
    const emailPhone = type === "email" ? auth?.profile?.email : auth?.profile?.phonenumber;

    sendMultiFactorCode(emailPhone, type)
      .then(() => {
        if (showMessage) setSuccessMsg("Verification code sent.");
      })
      .catch((err: any) => {
        if (!err?.response) setErrMsg("No server response.");
        else setErrMsg("Failed to send code.");
      });
  };

  useEffect(() => {
    if (auth?.twofactor) {
      if (auth?.profile?.homepage) {
        setActivePage(auth.profile.homepage);
        navigate(auth.profile.homepage);
      } else {
        navigate("/");
      }
    }
  }, [auth]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    verifyTwoFactor(code)
      .then((res: any) => {
        setAuth({ ...auth, twofactor: true });
      })
      .catch((err: any) => {
        var _attempts = attempts + 1;
        setAttempts(_attempts);
        if (!err?.response) {
          setErrMsg("No Server Response");
        } else if (err?.response?.status === 418) {
          setErrMsg("Invalid Code. Attempt " + _attempts + "/3");
          if (_attempts === 3) {
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } else {
          setErrMsg("Login Failed");
        }
      });
  };

  const handleLogout = () => {
    logout()
      .then((res: any) => {
        navigate("/login");
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  return (
    <div className="h-100 app-primary-alt-color align-content-center">
      <Row className="w-100 pb-5 d-flex align-item-center justify-content-center m-0">
        <Col xs={10} sm={8} md={6} lg={5} xl={4} xxl={4} className="pb-5">
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <p className={successMsg ? "successmsg-inverse" : "offscreen"} aria-live="assertive">
            {successMsg}
          </p>
          <Card className="shadow-sm rounded-lg d-block">
            <Image src="/AndGo_Logo_Color_Horizontal.png" fluid className="pt-5 px-5 pb-5" onClick={() => handleLogout()} />
            <Form onSubmit={handleSubmit}>
              <h6 className="text-center text-secondary mb-3 mx-3">
                {(() => {
                  switch (auth?.profile?.default_2fa) {
                    case "email":
                      return "Enter the verification code sent to your email.";
                    case "phone":
                      return "Enter the verification code sent to your phone.";
                    default:
                      return "Open your two-step verification app to get your verification code.";
                  }
                })()}
              </h6>
              <FloatingLabel label="Code" className="mx-3 mb-3 app-header-text">
                <Form.Control
                  type="text"
                  placeholder="Code"
                  id="user"
                  onChange={(e) => {
                    setCode(e.target.value);
                    setErrMsg("");
                  }}
                  value={code}
                  ref={inputRef}
                  required
                />
              </FloatingLabel>
              <div className="d-grid w-100">
                <Button className="button app-tertiary-color text-center mx-3 mb-3 p-3" type="submit">
                  Authenticate
                </Button>
              </div>
              <p className="d-flex justify-content-center gap-1 text-secondary">
                Send code via
                <Alert.Link onClick={() => sendCode("email", true)}> email </Alert.Link>
                or
                <Alert.Link onClick={() => sendCode("phone", true)}> text message </Alert.Link>
                instead.
              </p>
              <Alert
                key="back"
                variant="light"
                className="p-1 text-center border-0 bg-transparent"
              >
                <Alert.Link onClick={handleLogout}>Back to login</Alert.Link>
              </Alert>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Authenticate;
